import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Heart, Mail } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import ExternalStores from "@/components/ExternalStores";
import SocialWall from "@/components/SocialWall";
import NewsletterForm from "@/components/NewsletterForm";

const HERO = "https://images.pexels.com/photos/10317493/pexels-photo-10317493.jpeg";
const ABOUT_IMG = "https://images.unsplash.com/photo-1713845784497-fe3d7ed176d8";
const CAUSE_IMG = "https://images.pexels.com/photos/14500291/pexels-photo-14500291.jpeg";

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } })
      .then((r) => setFeatured(r.data.slice(0, 4)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center" data-testid="hero-section">
        <img src={HERO} alt="DaviDÉ Editorial" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 w-full pt-32 z-10">
          <div className="max-w-2xl">
            <p className="overline mb-6">Vol. 01 — Heritage Reimagined</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-light text-white leading-[0.95] tracking-tighter">
              Cultural Attire,<br />
              <span style={{color:"var(--davide-gold)"}}>Modern</span> Confidence.
            </h1>
            <p className="text-base md:text-lg text-white/75 mt-8 max-w-xl leading-relaxed">
              DaviDÉ Global Fashion weaves Ankara, Adire, and contemporary streetwear into pieces that make a statement — for those who wear their heritage with pride.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/shop" className="btn-gold" data-testid="hero-shop-btn">Shop the Collection <ArrowRight size={16}/></Link>
              <Link to="/community" className="btn-outline-gold" data-testid="hero-cause-btn">Our Cause</Link>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE STRIP */}
      <section className="border-y border-white/10 bg-[#0E0E0E] py-5 overflow-hidden">
        <div className="flex gap-12 items-center justify-around text-[var(--davide-gold)] font-display text-xl md:text-2xl whitespace-nowrap">
          <span>Ankara · Adire · Heritage</span>
          <span className="text-white/30">◆</span>
          <span>Worldwide Shipping</span>
          <span className="text-white/30">◆</span>
          <span>Crafted with Confidence</span>
          <span className="text-white/30 hidden md:inline">◆</span>
          <span className="hidden md:inline">Limited Editorial Drops</span>
        </div>
      </section>

      {/* FEATURED COLLECTIONS — bento */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24" data-testid="featured-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="overline mb-4"><Sparkles size={14} className="inline mr-1.5" /> Editor's Picks</p>
            <h2 className="font-display text-4xl md:text-6xl font-light text-white tracking-tight">Signature Pieces.</h2>
          </div>
          <Link to="/shop" className="text-sm uppercase tracking-widest text-[var(--davide-gold)] hover:underline" data-testid="see-all-link">
            See all products →
          </Link>
        </div>

        {featured.length === 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => <div key={i} className="aspect-[3/4] bg-[#141414] animate-pulse"/>)}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* BRAND STORY */}
      <section className="bg-[#0E0E0E] py-24" data-testid="brand-story-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <div className="relative">
            <img src={ABOUT_IMG} alt="Brand portrait" className="w-full h-[60vh] object-cover" />
            <div className="ankara-strip absolute -bottom-3 left-6 right-6" />
          </div>
          <div>
            <p className="overline mb-4">The DaviDÉ Story</p>
            <h2 className="font-display text-4xl md:text-5xl font-light text-white leading-tight">
              Tradition, retold for a global stage.
            </h2>
            <p className="text-white/70 mt-6 leading-relaxed text-base md:text-lg">
              Born from the studios of Lagos and refined for the streets of New York, London and Paris — DaviDÉ is a love letter to African craftsmanship. Each piece honors the master tailors, indigo dyers, and pattern-makers whose hands have shaped our heritage.
            </p>
            <Link to="/about" className="btn-outline-gold inline-block mt-8" data-testid="learn-more-btn">Read Our Story</Link>
          </div>
        </div>
      </section>

      {/* YAAIDFI CAUSE */}
      <section className="relative py-24 overflow-hidden" data-testid="cause-section">
        <img src={CAUSE_IMG} alt="YAAIDFI community" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="max-w-xl">
            <p className="overline mb-4"><Heart size={14} className="inline mr-1.5" /> A portion of every sale</p>
            <h2 className="font-display text-4xl md:text-5xl font-light text-white leading-tight">
              Supports <span style={{color:"var(--davide-red)"}}>YAAIDFI</span> youth health & empowerment.
            </h2>
            <p className="text-white/75 mt-6 leading-relaxed">
              Together with the YAAIDFI Foundation, we educate, empower and equip young people with knowledge to fight infectious diseases and build healthier communities across Africa.
            </p>
            <Link to="/community" className="btn-gold inline-flex mt-8" data-testid="learn-cause-btn">Discover the Cause <ArrowRight size={16}/></Link>
            <a href="https://www.yaaidfi.org" target="_blank" rel="noopener noreferrer"
              className="btn-outline-gold inline-flex mt-8 ml-3" data-testid="home-yaaidfi-btn">
              yaaidfi.org →
            </a>
          </div>
        </div>
      </section>

      {/* EXTERNAL STORES */}
      <ExternalStores />

      {/* SOCIAL WALL */}
      <SocialWall />

      {/* NEWSLETTER */}
      <section className="py-24 border-t border-white/10 bg-[#0A0A0A]" data-testid="newsletter-section">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <Mail size={32} className="text-[var(--davide-gold)] mx-auto mb-4" />
          <p className="overline mb-4">The Inner Circle</p>
          <h2 className="font-display text-4xl md:text-5xl font-light text-white leading-tight">
            First look at every drop.
          </h2>
          <p className="text-white/70 mt-4 max-w-lg mx-auto">
            New collections, private editorial previews, and YAAIDFI cause updates — straight to your inbox. No noise.
          </p>
          <div className="mt-8 flex justify-center">
            <NewsletterForm source="home" testId="home-newsletter" />
          </div>
        </div>
      </section>
    </div>
  );
}
